import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, Loader2, RotateCcw, Upload } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDisplayDateTime } from '@/utils/date';
import { papersQueryOptions } from '../api/question.api';
import { downloadPaperWithFeedback } from '../utils/downloadPaperWithFeedback';
import { useResetPapers, useUploadPaper } from '../hooks/useQuestionMutations';
import type { UploadSubject } from '../types/question.types';

interface PaperManagerDialogProps {
  subject: UploadSubject | null;
  instituteName: string | null;
  canWrite: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaperManagerDialog({
  subject,
  instituteName,
  canWrite,
  onOpenChange,
}: PaperManagerDialogProps) {
  const instituteSubjectId = subject?.instituteSubjectId;
  const { data, isLoading } = useQuery(papersQueryOptions(instituteSubjectId));

  const fileInput = useRef<HTMLInputElement>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const upload = useUploadPaper();
  const reset = useResetPapers(() => setConfirmReset(false));

  function handlePicked(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file === undefined || instituteSubjectId === undefined) return;

    upload.mutate({ instituteSubjectId, file });
    event.target.value = '';
  }

  const papers = data ?? [];

  return (
    <>
      <Dialog open={subject !== null} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{subject?.subjectName ?? 'Papers'}</DialogTitle>
            <DialogDescription>
              {instituteName ?? 'This institute'} ·{' '}
              {papers.length === 0
                ? 'nothing handed in yet'
                : `${papers.length} file${papers.length === 1 ? '' : 's'} handed in`}
            </DialogDescription>
          </DialogHeader>

          {canWrite ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInput}
                type="file"
                className="hidden"
                onChange={handlePicked}
                accept="image/*,.pdf"
              />
              <Button
                type="button"
                size="sm"
                onClick={() => fileInput.current?.click()}
                disabled={upload.isPending}
              >
                {upload.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Upload className="size-4" />
                )}
                Add a paper
              </Button>

              {papers.length === 0 ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmReset(true)}
                >
                  <RotateCcw className="size-4" />
                  Reset
                </Button>
              )}
            </div>
          ) : null}

          <div className="min-w-0 overflow-x-auto">
            {isLoading ? (
              <ListSkeleton />
            ) : papers.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="Nothing handed in yet"
                description="No paper has been handed in for this subject."
                size="compact"
              />
            ) : (
              <ul className="divide-y rounded-lg border">
                {papers.map((paper) => (
                  <li key={paper.id} className="flex items-center justify-between gap-3 p-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <FileText className="size-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm">{paper.fileName ?? 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDisplayDateTime(paper.uploadedAt)}
                          {paper.uploadedBy === null ? '' : ` · ${paper.uploadedBy}`}
                        </p>
                      </div>
                    </div>

                    {paper.hasFile ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void downloadPaperWithFeedback(paper.id, paper.fileName)}
                      >
                        <Download className="size-4" />
                        Download
                      </Button>
                    ) : (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        Recorded, not on this server yet
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Reset this subject?"
        description={`Every file handed in for "${subject?.subjectName ?? 'this subject'}" is removed, along with the record of who has opened them. The subject goes back to reading as never collected.`}
        confirmLabel="Reset"
        confirmVariant="destructive"
        isPending={reset.isPending}
        onConfirm={() => {
          if (instituteSubjectId !== undefined) reset.mutate(instituteSubjectId);
        }}
      />
    </>
  );
}
